import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withRouter, useHistory, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { If } from 'app/components/if';
import { GeneralConstants } from 'app/constants/general-constants';
import { useCallbackOnProdSwitch } from 'app/hooks';
import { Store } from 'app/model/cris/store';
import { searchService } from 'app/services/search-service';

import { StoreListGrid } from './store-list-grid';
import { selectProdDataStatus } from '../cris.slice';

const StoreSearch: React.FC = () => {
    const [stores, setStores] = useState<Store[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const showProd = useSelector(selectProdDataStatus);
    const queryRef = useRef<HTMLInputElement>();
    const location = useLocation();
    const history = useHistory();

    const findStores = useCallback(() => {
        if (!queryRef.current) {
            return;
        }
        const searchTerm = queryRef.current.value;

        searchService
            .searchStores(searchTerm, 1, GeneralConstants.PAGE_SIZE_HUGE)
            .subscribe((pageData) => setStores(pageData.data));

        const queryParams = new URLSearchParams(location.search);
        if (searchTerm) {
            queryParams.set('term', searchTerm);
        } else {
            queryParams.delete('term');
        }
        history.push({
            search: queryParams.toString() ? `?${queryParams.toString()}` : '',
        });
    }, [location.search, history]);

    const handleSubmit = (evt: React.FormEvent): void => {
        evt.preventDefault();
        findStores();
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const term = queryParams.get('term');

        if (term && queryRef.current) {
            queryRef.current.value = term;
            findStores();
        }
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    useCallbackOnProdSwitch(findStores);

    return (
        <React.Fragment>
            <Typography variant='body1' className='mb-1'>
                Search for CRIS Stores within the system. Search field accepts brand or store name.
            </Typography>
            <form onSubmit={handleSubmit}>
                <Box display='flex' mb={1}>
                    <Box flexGrow={1} mr={1}>
                        <TextField
                            name='term'
                            label='Search Term'
                            variant='outlined'
                            size='small'
                            inputRef={queryRef}
                            fullWidth
                        />
                    </Box>
                    <Button variant='contained' color='primary' type='submit' onClick={findStores}>
                        Search
                    </Button>
                    <If condition={!showProd}>
                        <Button
                            type='button'
                            variant='outlined'
                            color='warning'
                            className='ml-1'
                            component={NavLink}
                            to='/cris/stores/new/create'
                        >
                            Create Store
                        </Button>
                    </If>
                </Box>
            </form>
            <StoreListGrid
                rows={stores}
                paginationModel={{
                    pageSize: 50,
                    page: 0,
                }}
                disableRowSelectionOnClick
                density='compact'
                pagination
            />
        </React.Fragment>
    );
};
const connected = withRouter(StoreSearch);

export { connected as StoreSearch };
